<template>
    <font-awesome-icon v-if="isLoading" icon="spinner" spin/>
    <div v-else>
        <b-row class="mb-4">
            <b-col cols="12" md="6">
                <b-card>
                    <b-card-title>METADATA</b-card-title>

                    <b-table-simple small>
                        <colgroup>
                            <col style="width:250px;"/>
                            <col/>
                        </colgroup>
                        <b-tr>
                            <b-th variant="light">Aangemaakt op</b-th>
                            <b-td>{{ goodsReceipt.created_at | datetime }}</b-td>
                        </b-tr>
                        <b-tr>
                            <b-th variant="light">Identifier</b-th>
                            <b-td>{{ goodsReceipt.identifier }}</b-td>
                        </b-tr>
                        <b-tr>
                            <b-th variant="light">Leverancier</b-th>
                            <b-td>
                                <location-link :location="goodsReceipt.supplier_location"/>
                            </b-td>
                        </b-tr>
                        <b-tr>
                            <b-th variant="light">Ontvanger</b-th>
                            <b-td>
                                <location-link :location="goodsReceipt.recipient_location"/>
                            </b-td>
                        </b-tr>
                        <b-tr>
                            <b-th variant="light">Voltooid</b-th>
                            <b-td>
                                {{ goodsReceipt.is_completed ? 'Ja' : 'Nee' }}
                            </b-td>
                        </b-tr>
                    </b-table-simple>
                </b-card>
            </b-col>

            <b-col v-if="!goodsReceipt.is_completed" cols="12" md="6" class="text-right">
                <b-button v-if="this.hasPermission('orders.batch.process.sort')" :disabled="isSaving || goodsReceipt.is_completed"
                          :to="getScanRoute()"
                          class="bg-midnight-bloom text-white"
                >
                    Start met scannen
                </b-button><br />
                <b-button v-if="this.hasPermission('orders.batch.process.sort')" class="mt-2" :disabled="isSaving || goodsReceipt.is_completed"
                          @click="$bvModal.show('confirmPostProcess')"
                          variant="info"
                >
                    <span v-if="!isSaving">Klaar met scannen</span>
                    <font-awesome-icon v-else icon="spinner" spin/>
                </b-button>
            </b-col>
        </b-row>

        <b-row class="mb-4">
            <b-col>
                <b-card>
                    <b-card-title>PRODUCTEN</b-card-title>

                    <b-table :fields="productFields" :items="goodsReceipt.lines" small>
                        <template #table-colgroup>
                            <col style="width:130px;"/>
                            <col style=""/>
                            <col style="width:200px;"/>
                            <col style="width:200px;"/>
                            <col style="width:150px;"/>
                            <col style="width:150px;"/>
                        </template>
                        <template #cell(product_identifier)="data">
                            <product-number :number="data.value" :product-name="data.item.metadata?.pim_data?.attributes?.productnaam_logistiek ?? data.item.metadata?.pim_data?.attributes?.product_naam"/>
                        </template>
                        <template #cell(product_name)="data">
                            {{ data.item.metadata?.pim_data?.attributes?.productnaam_logistiek ?? data.item.metadata?.pim_data?.attributes?.product_naam ?? '' }}
                        </template>
                        <template #cell(is_bio)="data">
                            {{ (data.item.metadata?.pim_data?.attributes?.is_biologisch) ? 'Ja' : 'Nee' }}
                        </template>
                    </b-table>
                </b-card>
            </b-col>
        </b-row>

        <b-row v-if="unprocessedDispatchRequests.length">
            <b-col>
                <b-card>
                    <b-card-title>LEVERINGEN MET ONVERWERKTE PRODUCTEN</b-card-title>

                    <div v-for="(dispatchRequests, orderId) in unprocessedDispatchRequestsByOrder" :key="orderId">
                        <h5>
                            {{dispatchRequests[0].sales_order.identifier}}
                            <b-button variant="link" v-b-toggle="orderId">
                                <font-awesome-icon icon="caret-down" class="when-closed"/>
                                <font-awesome-icon icon="caret-up" class="when-open"/>
                            </b-button>
                        </h5>

                        <b-collapse :id="orderId">
                            <b-link :to="orderLink(dispatchRequests[0].sales_order.identifier)" target="_blank">Ga naar order</b-link>

                            <dispatch-requests :dispatch-requests="dispatchRequests" :sales-order="dispatchRequests[0].sales_order"/>
                        </b-collapse>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <b-modal id="confirmPostProcess"
                 title="Klaar met scannen"
                 cancel-title="Annuleren"
                 ok-title="Doorgaan"
                 @ok="postProcess"
                 centered
                 dialog-class="shadow-none"
        >
            Weet je zeker dat je deze goederenontvangst wilt afronden?<br/>
            Alle niet-gescande producten zullen bij hun orders opnieuw worden toegewezen aan een nieuwe crossdock-levering.
        </b-modal>
    </div>
</template>

<script>
import LocationLink from "@/components/LocationLink.vue";
import _ from "lodash"
import {BATCH_MODEL_TYPE_GOODS_RECEIPT} from "@/data/batchModelTypes";
import {PRODUCT_PROCESS_TYPE_SORT} from "@/data/productProcessType";
import DispatchRequests from "../../OrderDetail/components/DispatchRequests.vue";
import ProductNumber from "../../../components/ProductNumber.vue";

export default {
    name: "GoodsReceiptView",
    components: {ProductNumber, DispatchRequests, LocationLink},

    props: {
        location: {required: false},
        goodsReceiptIdentifier: {required: true, type: String},
    },

    data() {
        return {
            isLoading: true,
            isSaving: false,
            goodsReceipt: {},
            productFields: [
                {
                    key: 'product_identifier',
                    label: 'Identifier',
                },
                {
                    key: 'product_name',
                    label: 'Naam',
                    sortable: true,
                },
                {
                    key: 'quantity',
                    label: 'Aantal goederenontvangst',
                    sortable: true,
                },
                {
                    key: 'current_dispatch_request_lines',
                    label: 'Aantal crossdock',
                    sortable: true,
                    formatter: (lines) => _.sumBy(lines, 'quantity')
                },
                {
                    key: 'quantity_scanned',
                    label: 'Aantal gescand',
                    sortable: true,
                },
                {
                    key: 'is_bio',
                    label: 'Bio product',
                    sortable: true,
                },
            ],
            unprocessedDispatchRequests: [],
        }
    },

    computed: {
        unprocessedDispatchRequestsByOrder() {
            return _.groupBy(this.unprocessedDispatchRequests, 'sales_order_id')
        },
    },

    mounted() {
        this.getGoodsReceipt()
    },

    methods: {
        async getGoodsReceipt() {
            return this.$http.get(`/api/rest/locations/${this.location.location_type.identifier}/${this.location.identifier}/goods-receipts/${this.goodsReceiptIdentifier}`, {
                params: {
                    include: [
                        'lines.current_dispatch_request_lines',
                        'supplier_location.location_type',
                        'recipient_location.location_type',
                    ].join(',')
                }
            })
                .then(response => {
                    this.goodsReceipt = response.data
                    this.isLoading = false
                })
        },
        getScanRoute() {
            return {
                name: 'ProcessStartWithBatch',
                params: {
                    locationTypeIdentifier: this.location.location_type.identifier,
                    locationIdentifier: this.location.identifier,
                    batchModelType: BATCH_MODEL_TYPE_GOODS_RECEIPT,
                    batchModelIdentifier: this.goodsReceiptIdentifier,
                    processType: PRODUCT_PROCESS_TYPE_SORT,
                },
            }
        },
        async postProcess() {
            this.isSaving = true
            return this.$http.post(`/api/rest/locations/${this.location.location_type.identifier}/${this.location.identifier}/goods-receipts/${this.goodsReceiptIdentifier}/post-process`)
                .then(response => {
                    this.isSaving = false
                    this.goodsReceipt.is_completed = true
                    this.unprocessedDispatchRequests = response.data
                    this.$alert('Goederenontvangst afgerond.')
                })
                .catch(e => {
                    this.isSaving = false
                    this.$alert('Fout bij het afronden van de goederenontvangst. Probeer het opnieuw, of neem contact op met OmniStock.', 'danger')
                })
        },
        orderLink(orderIdentifier) {
            return {
                name: 'OrderDetailOverview',
                params: {
                    salesOrderIdentifier: orderIdentifier,
                }
            }
        },
    },
}
</script>

<style scoped>
.collapsed > .when-open,
.not-collapsed > .when-closed {
    display: none;
}
</style>
